import { useId, type ReactNode } from "react";
import { AppLink, Avatar, EmptyState, Skeleton, cx } from "@amber/ui";
import type { SearchResponse } from "@amber/application/contracts";
import { LIFECYCLE_LABELS, Snippet, itemMeta, itemStatus, passageMeta, statusTone, timeLabel } from "./hits";

function Section({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count: number;
  action?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="mb-6">
      <div className="mb-1.5 flex items-baseline gap-2 border-b border-divider pb-1.5">
        <h2 id={id} className="text-lg font-semibold">
          {title} <span className="font-mono text-md font-normal text-muted">{count}</span>
        </h2>
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

export function SearchResults({
  q,
  data,
  filtered,
}: {
  q: string;
  data: SearchResponse | undefined;
  filtered: boolean;
}) {
  if (q.length === 0) {
    return (
      <EmptyState title="Type something to search">
        Search covers item titles, transcripts, and people with their aliases. Press / anywhere to search.
      </EmptyState>
    );
  }
  if (!data) {
    return (
      <div aria-busy="true" className="flex flex-col gap-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
    );
  }
  if (data.url) return <UrlResult data={data} />;
  if (data.items.total + data.passages.total + data.people.total === 0) {
    return (
      <EmptyState title={`Nothing matches “${q}”`}>
        {filtered
          ? "Try removing the transcript filters."
          : "Search covers item titles, transcripts, and people with their aliases."}
      </EmptyState>
    );
  }
  return (
    <>
      <Section title="Transcripts" count={data.passages.total}>
        {data.passages.rows.length === 0 ? (
          <p className="py-2 text-md text-muted">No transcript passages match{filtered ? " these filters" : ""}.</p>
        ) : null}
        <ul className="m-0 list-none p-0">
          {data.passages.rows.map((p) => (
            <li key={p.unit_id} className="border-b border-divider py-2 last:border-b-0">
              <AppLink href={p.href} className="block rounded-md px-1.5 py-0.5 text-fg no-underline hover:bg-surface">
                <span className="flex items-baseline gap-2">
                  <span className="font-strong">{p.speaker}</span>
                  <span className="text-muted">· {timeLabel(p.start_ms) ?? "text"}</span>
                  <span className="flex-1" />
                  {p.readiness ? (
                    <span
                      className={cx(
                        "text-md whitespace-nowrap",
                        p.readiness.result === "eligible" ? "text-fg" : "text-muted",
                      )}
                    >
                      {p.readiness.label}
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 block text-base">
                  <Snippet segments={p.snippet} />
                </span>
                <span className="mt-0.5 block text-md text-muted">{passageMeta(p)}</span>
              </AppLink>
            </li>
          ))}
        </ul>
        <More shown={data.passages.rows.length} total={data.passages.total} />
      </Section>
      {data.items.total === 0 ? null : (
        <Section
          title="Items"
          count={data.items.total}
          action={
            data.items.total > 0 ? (
              <AppLink href={`/backlog?q=${encodeURIComponent(q)}`} className="text-md font-strong text-link">
                Open in Backlog
              </AppLink>
            ) : null
          }
        >
          <ul className="m-0 list-none p-0">
            {data.items.rows.map((item) => (
              <li key={item.id} className="border-b border-divider py-1.5 last:border-b-0">
                <AppLink
                  href={`/backlog/${item.id}`}
                  className="flex items-baseline gap-3 rounded-md px-1.5 py-0.5 text-fg no-underline hover:bg-surface"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-strong">{item.title}</span>
                    <span className="block truncate text-md text-muted">{itemMeta(item)}</span>
                  </span>
                  <span className={cx("flex-none text-md whitespace-nowrap", statusTone(item.status))}>
                    {itemStatus(item)}
                  </span>
                </AppLink>
              </li>
            ))}
          </ul>
          <More shown={data.items.rows.length} total={data.items.total} />
        </Section>
      )}
      {data.people.total === 0 ? null : (
        <Section title="People" count={data.people.total}>
          <ul className="m-0 list-none p-0">
            {data.people.rows.map((p) => (
              <li key={p.id} className="border-b border-divider py-1.5 last:border-b-0">
                <AppLink
                  href={`/backlog?person=${p.id}`}
                  className="flex items-center gap-2.5 rounded-md px-1.5 py-0.5 text-fg no-underline hover:bg-surface"
                >
                  <Avatar name={p.name} />
                  <span className="min-w-0 flex-1">
                    <span className="block font-strong">{p.name}</span>
                    <span className="block text-md text-muted">
                      {p.alias ? `Also known as ${p.alias}` : "Opens their items in the Backlog"}
                    </span>
                  </span>
                  <span className="text-md text-muted">{LIFECYCLE_LABELS[p.lifecycle_state]}</span>
                </AppLink>
              </li>
            ))}
          </ul>
          <More shown={data.people.rows.length} total={data.people.total} />
        </Section>
      )}
    </>
  );
}

function More({ shown, total }: { shown: number; total: number }) {
  if (shown >= total) return null;
  return (
    <p className="mt-1.5 text-md text-muted">
      Showing the first {shown} of {total}. Add words or filters to narrow the results.
    </p>
  );
}

function UrlResult({ data }: { data: SearchResponse }) {
  const u = data.url;
  if (!u) return null;
  return (
    <div className="rounded-xl border border-divider p-4">
      <div className="mb-2 font-mono text-md break-all text-muted">{u.canonical_url ?? data.q}</div>
      <p className="m-0 font-strong">{u.message}</p>
      <div className="mt-3 flex gap-3">
        {u.item ? (
          <>
            <AppLink href={`/backlog/${u.item.id}`}>Show it in the Backlog</AppLink>
            <AppLink href={`/items/${u.item.id}`}>Open the evidence record</AppLink>
          </>
        ) : null}
        {u.outcome === "new" ? (
          data.can_add.enabled ? (
            <AppLink href={`/backlog?add=${encodeURIComponent(data.q)}`}>Add this URL…</AppLink>
          ) : (
            <span className="text-md text-muted">{data.can_add.reason}</span>
          )
        ) : null}
      </div>
    </div>
  );
}
